import { Check, Copy } from "lucide-react";
import { Dialog, DialogContent } from "@/modern/components/ui/dialog-shadcn";
import { Button } from "@/modern/components/ui/button-shadcn";
import { cn } from "@/modern/utils";
import { useContextForm } from "../context/ContextFormProvider";
import { BoardContextWizardSteps } from "./wizard/BoardContextWizardSteps";
import { contextDisplayName } from "../utils/contextDisplayName";
import type { ModalMode } from "../context/ContextFormProvider";

interface AddContextModalProps {
  open: boolean;
  mode: ModalMode;
  contextId: number | null;
  onHide: () => void;
  onHandoff: (finishedId: number) => void;
}

export const AddContextModal = ({
  open,
  mode,
  onHide,
  onHandoff,
}: AddContextModalProps) => {
  const { boardContextRecord, groups, currentStep } = useContextForm();

  const hasGroups = groups.some((g) => !g.isPreview);
  const isExtendIdle = mode === "extend" && currentStep === "context-view";
  const effectiveStep = currentStep == null ? "setup" : (isExtendIdle ? "setup" : currentStep);
  const isSummaryStep = effectiveStep === "context-view" && hasGroups;
  const setupVariant = mode === "create" ? "create" : "add-context";
  const showContextName = mode !== "create" && boardContextRecord?.name;

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      if (isSummaryStep && boardContextRecord?.id != null) {
        onHandoff(boardContextRecord.id);
        return;
      }
      onHide();
    }
  };

  const handleDone = () => {
    if (boardContextRecord?.id != null) {
      onHandoff(boardContextRecord.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          "sm:max-w-[920px] h-[min(720px,92vh)] gap-0 p-0 overflow-hidden flex flex-col",
          isSummaryStep && "pb-0"
        )}
      >
        {showContextName && (
          <div className="shrink-0 flex items-center px-6 h-12 border-b border-border/40">
            <p className="text-base font-semibold text-foreground truncate">
              {contextDisplayName(boardContextRecord.name)}
            </p>
          </div>
        )}

        {mode === "extend" && boardContextRecord?.isActive && effectiveStep === "setup" && (
          <div className="shrink-0 flex items-center gap-3 px-5 py-2.5 bg-amber-500/10 border-b border-amber-500/20">
            <Copy className="h-4 w-4 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
            <p className="text-xs text-amber-700 dark:text-amber-300 leading-snug">
              This context is active and read-only. Adding context will create a copy. Changes are applied to the copy, not the original.
            </p>
          </div>
        )}

        <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
          <BoardContextWizardSteps
            step={effectiveStep}
            includeContextView
            setupOptions={{
              variant: setupVariant,
              onCancel: () => handleOpenChange(false),
              onSuggestionsStarted: onHide,
            }}
          />
        </div>

        {isSummaryStep && (
          <div className="shrink-0 flex items-center justify-end gap-2 px-4 py-3 border-t border-border/40 bg-card">
            <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
            <Button size="sm" onClick={handleDone} className="gap-1.5">
              <Check className="h-3.5 w-3.5" />
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
